import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, type UserSearchResult } from "../api/client";
import UserPicker from "../components/UserPicker";

export default function NewClaimPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });
  const { data: costCentres } = useQuery({ queryKey: ["cost-centres"], queryFn: api.costCentres });

  const [costCentreId, setCostCentreId] = useState<number | null>(null);
  const [costCentreTouched, setCostCentreTouched] = useState(false);

  // Preselect from Entra-derived suggestion as a DEFAULT — user can change.
  useEffect(() => {
    if (!costCentreTouched && me?.suggested_cost_centre?.id && costCentreId == null) {
      setCostCentreId(me.suggested_cost_centre.id);
    }
  }, [me, costCentreTouched, costCentreId]);

  const suggestedReason = me?.suggested_cost_centre?.reason;
  const suggestedName = costCentres?.find((c) => c.id === me?.suggested_cost_centre?.id)?.name;
  const [overriding, setOverriding] = useState(false);
  const [pickedManager, setPickedManager] = useState<UserSearchResult | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const useOverride = overriding && pickedManager;
      return api.expenses.create({
        cost_centre_id: costCentreId!,
        manager_overridden: useOverride ? true : undefined,
        manager_entra_oid: useOverride ? pickedManager!.oid : undefined,
        manager_email: useOverride ? pickedManager!.email : undefined,
        manager_override_reason: useOverride ? overrideReason : undefined,
      });
    },
    onSuccess: (req) => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      nav(`/claim/${req.id}`);
    },
    onError: (e) => setError((e as Error).message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!costCentreId) return setError("Pick a cost centre");
    if (overriding) {
      if (!pickedManager) return setError("Pick a manager from the search results");
      if (!overrideReason.trim()) return setError("Reason required when overriding manager");
    } else if (!me?.manager) {
      return setError("No manager found in Entra — pick one below");
    }
    create.mutate();
  };

  // If no Entra manager exists, force the picker into override mode by default
  const noEntraManager = !me?.manager;
  const showPicker = overriding || noEntraManager;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-liaise-navy">New claim</h1>
        <p className="text-slate-600 mt-1">Step 1 of 2 — set up the claim header. You'll add lines next.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Cost centre <span className="text-red-500">*</span>
          </label>
          <select
            value={costCentreId ?? ""}
            onChange={(e) => { setCostCentreTouched(true); setCostCentreId(Number(e.target.value) || null); }}
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
            required
          >
            <option value="">Select a home or office…</option>
            <optgroup label="Support Office">
              {costCentres
                ?.filter((cc) => cc.type === "support_office")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.name}</option>
                ))}
            </optgroup>
            <optgroup label="Homes">
              {costCentres
                ?.filter((cc) => cc.type === "home")
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.name}</option>
                ))}
            </optgroup>
          </select>
          <p className="text-xs text-slate-500 mt-1">
            For mileage claims, this should be where you were travelling to/from.
          </p>
          {suggestedName && !costCentreTouched && costCentreId === me?.suggested_cost_centre?.id && (
            <p className="text-xs text-emerald-700 mt-1">
              Pre-selected from your Entra profile ({suggestedReason}). Change above if it's not right.
            </p>
          )}
        </div>

        <div className="border-t border-slate-100 pt-5">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Manager</label>

          {!showPicker ? (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm">
              <div className="font-medium text-slate-700">{me!.manager!.displayName}</div>
              <div className="text-slate-500">{me!.manager!.email}</div>
              <button
                type="button"
                onClick={() => setOverriding(true)}
                className="text-liaise-navy text-sm font-medium mt-2 hover:underline"
              >
                Change manager
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <UserPicker
                selected={pickedManager}
                onChange={setPickedManager}
                placeholder="Search by name or email…"
                excludeOids={
                  // Block self-as-manager unless the user is an Admin (kept for IT/testing)
                  me && !me.roles.includes("Admin") ? [me.oid] : []
                }
              />
              {overriding && (
                <input
                  type="text"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Reason for overriding (e.g. covering manager on leave)"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:border-liaise-navy focus:ring-1 focus:ring-liaise-navy outline-none"
                />
              )}
              {!noEntraManager && (
                <button
                  type="button"
                  onClick={() => {
                    setOverriding(false);
                    setPickedManager(null);
                    setOverrideReason("");
                  }}
                  className="text-slate-500 text-sm hover:underline"
                >
                  Use manager from Entra instead ({me!.manager!.displayName})
                </button>
              )}
            </div>
          )}
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={create.isPending}
            className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50 transition"
          >
            {create.isPending ? "Creating…" : "Continue to add lines"}
          </button>
          <button
            type="button"
            onClick={() => nav("/")}
            className="text-slate-600 px-5 py-2.5 rounded-lg hover:bg-slate-100 transition"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
